import type { ComponentChildren } from 'preact';
import mergeRefs from 'merge-refs';
import { useRevealAnimation } from 'utils/revealAnimation.ts';
import { cn } from 'utils/cn.tsx';
import { Icon } from 'components/atoms/Icon.tsx';
import { Dropdown } from 'components/dropdown/Dropdown.tsx';
import { localize } from 'i18n/localize.ts';
import { useFocusTrap } from 'utils/focusTrap';
import { useEffect, useState } from 'preact/hooks';

const $t = localize();

export const CommonContainer = ({
	mobileTitle = $t.offerMoreActions.title,
	isOpen,
	children,
	toggleOpen,
	dropdownRef,
	placement,
	dataTest,
	className,
	overlayChildren,
	overlayChildrenClassName,
	aria,
	id,
	isMobileFullscreen,
}: Dropdown.RenderContainerProps & { overlayChildren?: ComponentChildren; overlayChildrenClassName?: string }) => {
	const { ref: animationRef, elementClassSignal } = useRevealAnimation({
		animationOpenClass: 'animate-transformFromBottom',
		animationCloseClass: 'animate-transformToBottom',
		isOpen,
	});
	const [firstElementRef, lastElementRef, containerElementRef] = useFocusTrap();
	const [isOpenDelayed, setIsOpenDelayed] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setTimeout(() => {
				setIsOpenDelayed(true);
			}, 0);
		} else {
			setIsOpenDelayed(false);
		}
	}, [isOpen]);

	return (
		<>
			<div
				class={cn(
					'md:hidden z-backdrop md:z-[90] fixed left-0 right-0 top-0 bottom-0 flex flex-col bg-neutral-35 pointer-events-none',
					!isOpen && 'hidden',
				)}
			/>
			{isOpen && <span title={$t.modal.windowStartAnchorTitle} ref={firstElementRef} tabIndex={0} />}
			<div
				{...aria}
				id={id}
				class={cn(
					`fixed bottom-0 translate-y-full h-max max-h-[80%] right-0 mx-2 md:mx-0 w-[calc(100%-16px)] px-6 pt-8 pb-6 md:p-2 md:w-[400px] md:animate-none md:absolute md:bottom-auto md:translate-y-0 md:max-h-none rounded-t-xl md:rounded-xl shadow-xl md:shadow-lg text-sm md:text-base bg-neutral-white font-medium z-backdrop md:z-[90] overflow-auto print:hidden pointer-events-auto`,
					!isOpen && 'md:hidden',
					!isOpenDelayed && 'md:opacity-0',
					elementClassSignal.value,
					isMobileFullscreen && 'max-h-none h-full m-0 w-full',
					placement === 'bottom-left' && 'md:right-0 md:top-full md:mt-2',
					placement === 'bottom-right' && 'md:left-0 md:right-0 md:top-full md:mt-2',
					placement === 'bottom-center' && 'md:left-2/4 md:-translate-x-2/4 md:right-0 md:top-7',
					placement === 'top-left' && 'md:right-0 md:bottom-6',
					placement === 'top-right' && 'md:left-0 md:bottom-full md:mb-2',
					placement === 'top-center' && 'md:left-2/4 md:-translate-x-2/4 md:right-0 md:bottom-6',
					className,
				)}
				ref={mergeRefs(animationRef, dropdownRef, containerElementRef)}
				data-test={dataTest}
			>
				<div class="flex md:hidden justify-between mb-3 text-base font-bold">
					{mobileTitle}{' '}
					<button
						class="p-1 text-dark-blue hover:text-dark-blue-50"
						onClick={toggleOpen}
						title={$t.modal.close}
						type="button"
					>
						<Icon type="close" size={24} />
					</button>
				</div>
				{children}
			</div>
			{overlayChildren && (
				<div
					class={cn(
						'z-backdrop pointer-events-none left-0 top-0 w-full h-full fixed',
						elementClassSignal.value,
						overlayChildrenClassName,
					)}
				>
					{overlayChildren}
				</div>
			)}
			{isOpen && <span title={$t.modal.windowEndAnchorTitle} ref={lastElementRef} tabIndex={0} />}
		</>
	);
};
